// Calculation types
export const CALC_TYPES = {
    COMPOUND: 'compound',
    SIMPLE: 'simple'
};

// Period types
export const PERIODS = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly'
};

// Get period multiplier
const getPeriodMultiplier = (period) => {
    switch (period) {
        case PERIODS.WEEKLY: return 7;
        case PERIODS.MONTHLY: return 30;
        default: return 1;
    }
};

// Main calculation function
export const calculateProfit = ({
    balance,
    investmentRatio,
    percentage,
    targetDay,
    calcType = CALC_TYPES.COMPOUND,
    period = PERIODS.DAILY,
    inflation = 0,
    currency = 1
}) => {
    const results = [];
    const periodMultiplier = getPeriodMultiplier(period);
    const initialBalance = Number(balance);
    const ratio = Number(investmentRatio) / 100;
    const rate = Number(percentage) / 100;
    const days = Number(targetDay);
    const annualInflation = Number(inflation) / 100;
    const dailyInflation = annualInflation / 365;

    let currentBalance = initialBalance * ratio;

    for (let day = 1; day <= days; day++) {
        let newBalance, profit;

        if (calcType === CALC_TYPES.COMPOUND) {
            // Compound interest
            newBalance = currentBalance + currentBalance * rate;
            profit = newBalance - currentBalance;
            const totalBalance = newBalance / ratio - profit * (1 / ratio) + profit;

            results.push({
                day: day * periodMultiplier,
                usdt: totalBalance,
                try: totalBalance * currency,
                profit: profit,
                realValue: totalBalance / Math.pow(1 + dailyInflation, day * periodMultiplier)
            });

            currentBalance = totalBalance * ratio;
        } else {
            // Simple interest
            profit = initialBalance * ratio * rate;
            const totalBalance = initialBalance + (profit * day);

            results.push({
                day: day * periodMultiplier,
                usdt: totalBalance,
                try: totalBalance * currency,
                profit: profit,
                realValue: totalBalance / Math.pow(1 + dailyInflation, day * periodMultiplier)
            });
        }
    }

    return results;
};

// Calculate days needed to reach target
export const calculateDaysToTarget = ({
    balance,
    investmentRatio,
    percentage,
    targetAmount,
    calcType = CALC_TYPES.COMPOUND
}) => {
    const initialBalance = Number(balance);
    const ratio = Number(investmentRatio) / 100;
    const rate = Number(percentage) / 100;
    const target = Number(targetAmount);

    if (calcType === CALC_TYPES.COMPOUND) {
        let currentBalance = initialBalance * ratio;
        let totalBalance = initialBalance;
        let days = 0;

        while (totalBalance < target && days < 10000) {
            const newBalance = currentBalance + currentBalance * rate;
            const profit = newBalance - currentBalance;
            totalBalance = newBalance / ratio - profit * (1 / ratio) + profit;
            currentBalance = totalBalance * ratio;
            days++;
        }

        return days < 10000 ? days : -1;
    } else {
        const dailyProfit = initialBalance * ratio * rate;
        const needed = target - initialBalance;
        return Math.ceil(needed / dailyProfit);
    }
};

// Risk analysis - calculate different scenarios
export const calculateRiskScenarios = ({
    balance,
    investmentRatio,
    percentage,
    targetDay,
    currency = 1
}) => {
    const scenarios = [
        { name: 'worst', rate: -10 },
        { name: 'bad', rate: -5 },
        { name: 'neutral', rate: 0 },
        { name: 'good', rate: Number(percentage) },
        { name: 'best', rate: Number(percentage) * 1.5 }
    ];

    return scenarios.map(scenario => {
        const results = calculateProfit({
            balance,
            investmentRatio,
            percentage: scenario.rate,
            targetDay,
            currency
        });

        const finalBalance = results.length > 0 ? results[results.length - 1].usdt : Number(balance);

        return {
            name: scenario.name,
            rate: scenario.rate,
            finalBalance,
            profit: finalBalance - Number(balance),
            profitPercent: ((finalBalance - Number(balance)) / Number(balance)) * 100
        };
    });
};

// Compare multiple scenarios
export const compareScenarios = (scenarios, currency = 1) => {
    return scenarios.map((scenario, index) => {
        const results = calculateProfit({
            ...scenario,
            currency
        });

        const finalBalance = results.length > 0 ? results[results.length - 1].usdt : Number(scenario.balance);

        return {
            id: index + 1,
            ...scenario,
            results,
            finalBalance,
            totalProfit: finalBalance - Number(scenario.balance)
        };
    });
};
